// Firestore helpers — the SPA's data layer. Reads/writes go browser→Firestore
// directly (rules-gated); the only server calls are /api/* (extract, scrape,
// geocode) for paid/compute work. Schema in docs/plans/property-tracking-ux.md
// + docs/plans/user-attributes.md.
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDoc,
  getDocs,
  writeBatch,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db, auth } from './firebase.js';
import { normalizeName, normalizeAttrValue, applyValueDelta, seedWrites, NUMBER, STRING } from './attrs.js';

export const PROPERTIES = 'properties';
export const SHARES = 'shares';
export const REQUESTS = 'requests';
export const ACCOUNT_SHARES = 'account_shares';
export const ACCOUNT_SHARE_LINKS = 'account_share_links';

// 128-bit random token — the share key. Randomness (not predictability) is
// what protects the link; the token IS the credential (rules key on it).
export function newShareToken() {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

/** Create a property (owner) + its shares workspace doc in one flow. An
 * active account share (docs/plans/account-sharing.md) additionally
 * materializes current partners as members of the new workspace
 * (owner-provision) so the property is immediately readable by them — the
 * "future properties" promise — and appends pid to the portfolio lists. */
export async function createProperty(uid, { title, sourceUrl = null, address = null, lat = null, lng = null, ownerName = null, status = null }) {
  const pid = doc(collection(db, PROPERTIES)).id;
  const token = newShareToken();
  const now = serverTimestamp();
  await setDoc(doc(db, PROPERTIES, pid), {
    owner_uid: uid,
    title,
    status: status || 'potential',
    source_url: sourceUrl,
    address,
    lat,
    lng,
    share_token: token,
    owner_name: ownerName || auth.currentUser?.displayName || auth.currentUser?.email || null,
    created_at: now,
    updated_at: now,
  });
  // Shares doc is registration only (property pointer, owner, active flag).
  // The shared surface — title/status/notes — reads the canonical property
  // doc directly (S4: the workspace mirror is retired).
  await setDoc(doc(db, SHARES, token), {
    property_id: pid,
    owner_uid: uid,
    active: true,
  });
  const accSnap = await getDoc(doc(db, ACCOUNT_SHARES, uid));
  if (accSnap.exists()) {
    const acc = accSnap.data();
    if (acc.active && acc.member_uids?.length) {
      const names = await accountMemberNames(acc.member_uids);
      await provisionMemberDocs({ [pid]: token }, acc.member_uids, names);
    }
    await updateDoc(doc(db, ACCOUNT_SHARES, uid), {
      property_ids: arrayUnion(pid),
      property_tokens: { ...(acc.property_tokens || {}), [pid]: token },
    });
  }
  return { pid, token };
}

/** Client-side dedup: any doc of mine already holding this source_url.
 * Owner-scoped query — the read rule (resource.data.owner_uid == auth.uid)
 * requires the query to be provably restricted to my docs, otherwise the
 * rules engine denies it (rules are not filters). */
export async function findPropertyBySourceUrl(uid, sourceUrl) {
  if (!sourceUrl) return null;
  const snap = await getDocs(query(
    collection(db, PROPERTIES),
    where('owner_uid', '==', uid),
    where('source_url', '==', sourceUrl),
  ));
  const first = snap.docs[0];
  return first ? { id: first.id, ...first.data() } : null;
}

/** Live list of my properties, newest first (composite index owner_uid+created_at). */
export function watchMyProperties(uid, onData) {
  const q = query(
    collection(db, PROPERTIES),
    where('owner_uid', '==', uid),
    orderBy('created_at', 'desc'),
  );
  return onSnapshot(q, (snap) =>
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
}

/** Watch a single property doc. Errors (no access, missing) → onData(null). */
export function watchProperty(pid, onData) {
  return onSnapshot(
    doc(db, PROPERTIES, pid),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.warn('watchProperty error', err);
      onData(null);
    },
  );
}

/** Watch a shared workspace (token = key). Errors → onData(null). */
export function watchShare(token, onData) {
  return onSnapshot(
    doc(db, SHARES, token),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.warn('watchShare error', err);
      onData(null);
    },
  );
}

/** Watch the notes thread of a property (S4: notes live on the canonical
 * doc, keyed by pid — not on the share token). */
export function watchNotes(pid, onData) {
  const q = query(
    collection(db, PROPERTIES, pid, 'notes'),
    orderBy('created_at', 'asc'),
  );
  return onSnapshot(q, (snap) =>
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
}

export async function addNote(pid, { authorUid, authorName, text }) {
  const nid = doc(collection(db, PROPERTIES, pid, 'notes')).id;
  await setDoc(doc(db, PROPERTIES, pid, 'notes', nid), {
    author_uid: authorUid,
    author_name: authorName,
    text,
    created_at: serverTimestamp(),
  });
  return nid;
}

/** Single-write edit of the canonical property doc (S4: the workspace mirror
 * and two-doc sync are retired — every edit is exactly one write). */
export async function updateProperty(pid, patch) {
  await updateDoc(doc(db, PROPERTIES, pid), { ...patch, updated_at: serverTimestamp() });
}

/** Join-on-open: add me as a permanent editor of a share. */
export async function joinShare(token, { uid, displayName }) {
  await setDoc(doc(db, SHARES, token, 'members', uid), {
    display_name: displayName || 'Guest',
    joined_at: serverTimestamp(),
  });
}

/** Remove a member (owner or self). */
export async function removeMember(token, uid) {
  await deleteDoc(doc(db, SHARES, token, 'members', uid));
}

/** Watch extraction progress for a request. */
export function watchRequest(rid, onData) {
  return onSnapshot(doc(db, REQUESTS, rid), (snap) =>
    onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
  );
}

/** Watch my profile (name/phone) — used by the account page + first-submission
 * details check. Missing doc → onData(null). */
export function watchMyDetails(uid, onData) {
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.warn('watchMyDetails error', err);
      onData(null);
    },
  );
}

/** One-shot read of my profile (null when missing). */
export async function getMyDetails(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Save my real details (name + phone) — required before extraction can run. */
export async function saveMyDetails(uid, { display_name, phone }) {
  await setDoc(
    doc(db, 'users', uid),
    { display_name: (display_name || '').trim(), phone: (phone || '').trim(), updated_at: serverTimestamp() },
    { merge: true },
  );
}

export async function getShare(token) {
  const snap = await getDoc(doc(db, SHARES, token));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Live editors list of a share. */
export function watchMembers(token, onData) {
  return onSnapshot(collection(db, SHARES, token, 'members'), (snap) =>
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
}

/** Owner-only: flip the share's active flag (per-property link revoke /
 * re-enable). Members keep their member docs — revoke blocks new joins
 * without cutting existing editors (account sharing revokes via
 * revokeAccountShare, which deletes the member docs). */
export async function setShareActive(token, active) {
  await updateDoc(doc(db, SHARES, token), { active });
}

export async function listMembers(token) {
  const snap = await getDocs(collection(db, SHARES, token, 'members'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── account sharing (docs/plans/account-sharing.md) ─────────────────────
// account_shares/{owner_uid} is the sharing profile (member_uids,
// property_ids + property_tokens mirror, link_token, attribute_defs);
// account_share_links/{token} is the shareable link doc (token = key).
// ACCESS is the materialized member docs under each shared property's
// workspace — the rules gate that; these helpers write the docs.
//
// property_tokens ({ pid: share_token }) is a small addition to the plan's
// data model: a joiner cannot read the owner's property docs pre-join
// (member-gated), so the join flow reads the tokens from the owner's profile
// instead — no rules change, purely a client-side mirror kept in the same
// writes as property_ids.

const READ_CHUNK = 20;

/** pid → share_token for the given property ids (best-effort chunked reads;
 * unreadable chunks are skipped). Chunked Promise.all of single getDoc
 * calls — the web SDK has no getAll; per-doc reads are exactly what the
 * rules support. */
async function tokensForProperties(pids) {
  const tokens = {};
  for (let i = 0; i < pids.length; i += READ_CHUNK) {
    const chunk = pids.slice(i, i + READ_CHUNK);
    try {
      const snaps = await Promise.all(chunk.map((pid) => getDoc(doc(db, PROPERTIES, pid))));
      for (const s of snaps) if (s.exists()) tokens[s.id] = s.data().share_token;
    } catch (e) {
      console.warn('property token chunk skipped', e?.message);
    }
  }
  return tokens;
}

/** Portfolio tokens of an account-share doc: its own property_tokens mirror,
 * or the property docs themselves when the mirror is absent. */
async function accountTokens(acc) {
  return acc?.property_tokens && Object.keys(acc.property_tokens).length
    ? acc.property_tokens
    : await tokensForProperties(acc?.property_ids || []);
}

/** Provision member docs for each uid under every property in `tokens`
 * (pid → share_token). The caller must be the property owner
 * (owner-provision rule) — or self-provisioning (join-on-open rule) for
 * their own uid. Failure is per-member, non-fatal. */
async function provisionMemberDocs(tokens, memberUids, names) {
  for (const pid of Object.keys(tokens || {})) {
    const t = tokens[pid];
    if (!t) continue;
    for (const m of memberUids || []) {
      try {
        await setDoc(doc(db, SHARES, t, 'members', m), {
          display_name: (names && names[m]) || 'Guest',
          joined_at: serverTimestamp(),
        });
      } catch (e) {
        console.warn('member provisioning skipped', pid, m, e?.message);
      }
    }
  }
}

/** Delete every member doc of `memberUid` under the properties in `tokens`.
 * The caller must be the property owner (owner-delete) or the member
 * themself (self-delete). Failure is per-member, non-fatal. */
async function removeMemberDocs(tokens, memberUid) {
  for (const t of Object.values(tokens || {})) {
    if (!t) continue;
    try {
      await deleteDoc(doc(db, SHARES, t, 'members', memberUid));
    } catch (e) {
      console.warn('member-doc delete skipped', memberUid, e?.message);
    }
  }
}

/** Best-effort display names for uids (users/{uid}.display_name), chunked
 * parallel reads; missing/denied reads → 'Guest'. */
export async function accountMemberNames(uids) {
  const names = {};
  const list = uids || [];
  for (let i = 0; i < list.length; i += 10) {
    await Promise.all(
      list.slice(i, i + 10).map(async (m) => {
        try {
          const s = await getDoc(doc(db, 'users', m));
          names[m] = (s.exists() && s.data().display_name) || 'Guest';
        } catch {
          names[m] = 'Guest';
        }
      }),
    );
  }
  return names;
}

/** Read property docs by id (chunked per-doc reads — the web SDK has no
 * getAll). Only readable docs are returned; denied/missing ones are
 * skipped, so a stale or cut property never blocks the rest of a partner's
 * portfolio. Returns [{ id, ...data }]. */
export async function getPropertiesByIds(pids) {
  const out = [];
  for (let i = 0; i < pids.length; i += READ_CHUNK) {
    const chunk = pids.slice(i, i + READ_CHUNK);
    const snaps = await Promise.all(
      chunk.map((pid) => getDoc(doc(db, PROPERTIES, pid)).catch(() => null)),
    );
    for (const s of snaps) if (s?.exists()) out.push({ id: s.id, ...s.data() });
  }
  return out;
}

/** One-shot read of an account-sharing profile (null when missing). */
export async function getAccountShare(uid) {
  const snap = await getDoc(doc(db, ACCOUNT_SHARES, uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Live read of an account-sharing profile (missing doc → onData(null)). */
export function watchAccountShare(uid, onData) {
  return onSnapshot(
    doc(db, ACCOUNT_SHARES, uid),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.warn('watchAccountShare error', err);
      onData(null);
    },
  );
}

/** Live list of account shares linked to me (provable array-contains query).
 * Each doc is a partner's profile — the merged-dashboard source (S3). */
export function watchAccountShares(uid, onData) {
  const q = query(collection(db, ACCOUNT_SHARES), where('member_uids', 'array-contains', uid));
  return onSnapshot(q, (snap) =>
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
}

/** One-shot read of a share link doc (token = key; public read). */
export async function getAccountShareLink(token) {
  const snap = await getDoc(doc(db, ACCOUNT_SHARE_LINKS, token));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Owner: create the account-sharing profile + link token (lazy, one per
 * user). Backfills property_ids (+ tokens) from the owner query and
 * attribute_defs from the owner's definitions (the recs/filter union mirror).
 * Idempotent: an existing profile just returns its link token, unless it has
 * none (e.g. created by a reciprocal join) — then it is repaired. */
export async function createAccountShare(uid) {
  const existing = await getAccountShare(uid);
  if (existing?.link_token) return existing.link_token;
  const token = newShareToken();
  const [propsSnap, defsSnap] = await Promise.all([
    getDocs(query(collection(db, PROPERTIES), where('owner_uid', '==', uid))),
    getDocs(collection(db, 'users', uid, 'attributes')),
  ]);
  const property_ids = propsSnap.docs.map((d) => d.id);
  const property_tokens = Object.fromEntries(propsSnap.docs.map((d) => [d.id, d.data().share_token]));
  const attribute_defs = Object.fromEntries(defsSnap.docs.map((d) => [d.id, d.data().type]));
  if (existing) {
    await updateDoc(doc(db, ACCOUNT_SHARES, uid), {
      link_token: token,
      active: true,
      property_ids,
      property_tokens,
      attribute_defs,
    });
  } else {
    await setDoc(doc(db, ACCOUNT_SHARES, uid), {
      owner_uid: uid,
      active: true,
      member_uids: [],
      property_ids,
      property_tokens,
      attribute_defs,
      link_token: token,
      created_at: serverTimestamp(),
    });
  }
  await setDoc(doc(db, ACCOUNT_SHARE_LINKS, token), {
    owner_uid: uid,
    owner_name: auth.currentUser?.displayName || auth.currentUser?.email || 'Guest',
    active: true,
    created_at: serverTimestamp(),
  });
  return token;
}

/** Join an account share via its link token (the token IS the key). Mutual:
 * self-join the owner's profile, create/update my own with the owner linked,
 * then materialize member docs BOTH directions (that's the access grant).
 * Anonymous joiners allowed (owner sign-off); idempotent for already-linked
 * accounts (the rules' self-join requires exactly one new element, so the
 * self-join is skipped when I'm already in member_uids). */
export async function joinAccountShare(linkToken, { uid, displayName }) {
  const link = await getAccountShareLink(linkToken);
  if (!link || link.active !== true) throw new Error('This share link is invalid or revoked.');
  const ownerUid = link.owner_uid;
  const ownerName = link.owner_name || 'Guest';
  const acc = await getAccountShare(ownerUid);
  if (!acc || acc.active !== true) throw new Error('This share is no longer active.');
  const members = acc.member_uids || [];
  if (!members.includes(uid)) {
    await updateDoc(doc(db, ACCOUNT_SHARES, ownerUid), { member_uids: arrayUnion(uid) });
  }
  // Reciprocal: my own profile (owner rule) + portfolio backfill.
  const propsSnap = await getDocs(query(collection(db, PROPERTIES), where('owner_uid', '==', uid)));
  const myPids = propsSnap.docs.map((d) => d.id);
  const myTokens = Object.fromEntries(propsSnap.docs.map((d) => [d.id, d.data().share_token]));
  const mine = await getAccountShare(uid);
  if (mine) {
    const patch = { member_uids: arrayUnion(ownerUid), property_tokens: { ...(mine.property_tokens || {}), ...myTokens } };
    if (myPids.length) patch.property_ids = arrayUnion(...myPids);
    await updateDoc(doc(db, ACCOUNT_SHARES, uid), patch);
  } else {
    await setDoc(doc(db, ACCOUNT_SHARES, uid), {
      owner_uid: uid,
      active: true,
      member_uids: [ownerUid],
      property_ids: myPids,
      property_tokens: myTokens,
      created_at: serverTimestamp(),
    });
  }
  // Materialize the access grants both directions.
  const ownerTokens = await accountTokens(acc);
  const myName = displayName || auth.currentUser?.displayName || auth.currentUser?.email || 'Guest';
  await provisionMemberDocs(ownerTokens, [uid], { [uid]: myName });          // me under the owner's properties (join-on-open)
  await provisionMemberDocs(myTokens, [ownerUid], { [ownerUid]: ownerName }); // owner under mine (owner-provision)
  return { ownerUid, ownerName };
}

/** Owner: revoke account sharing — cut ALL linked accounts immediately. The
 * account share + link go inactive and the member docs (the actual access
 * grants) are deleted in BOTH directions: partners lose my portfolio, I lose
 * theirs. member_uids survive, so re-enable restores access without re-join. */
export async function revokeAccountShare(uid) {
  const acc = await getAccountShare(uid);
  if (!acc) return;
  await updateDoc(doc(db, ACCOUNT_SHARES, uid), { active: false });
  if (acc.link_token) {
    try {
      await updateDoc(doc(db, ACCOUNT_SHARE_LINKS, acc.link_token), { active: false });
    } catch (e) {
      console.warn('link deactivate skipped', e?.message);
    }
  }
  const myTokens = await accountTokens(acc);
  for (const m of acc.member_uids || []) {
    await removeMemberDocs(myTokens, m); // partners out of my properties (owner-delete)
    let macc = null;
    try {
      macc = await getAccountShare(m);
    } catch {
      /* unreadable partner profile — nothing more to cut */
    }
    if (!macc) continue;
    const mt = await accountTokens(macc);
    await removeMemberDocs(mt, uid); // me out of their properties (self-delete)
  }
}

/** Owner: flip the account share + link active flag. Re-enabling re-creates
 * the member docs for current partners (owner-provision on my properties,
 * self-join on theirs) — access returns without anyone re-joining. */
export async function setAccountShareActive(uid, active) {
  const acc = await getAccountShare(uid);
  if (!acc) return;
  await updateDoc(doc(db, ACCOUNT_SHARES, uid), { active });
  if (acc.link_token) {
    try {
      await updateDoc(doc(db, ACCOUNT_SHARE_LINKS, acc.link_token), { active });
    } catch (e) {
      console.warn('link update skipped', e?.message);
    }
  }
  if (active) {
    const names = await accountMemberNames(acc.member_uids || []);
    const myTokens = await accountTokens(acc);
    await provisionMemberDocs(myTokens, acc.member_uids || [], names); // partners back under my properties
    // My own access to partners' portfolios (self-join while their workspaces are active).
    const myName = auth.currentUser?.displayName || auth.currentUser?.email || 'Guest';
    for (const m of acc.member_uids || []) {
      let macc = null;
      try {
        macc = await getAccountShare(m);
      } catch {
        continue;
      }
      if (!macc) continue;
      const mt = await accountTokens(macc);
      await provisionMemberDocs(mt, [uid], { [uid]: myName });
    }
  }
}

/** Owner: remove one linked account — cuts BOTH directions. Their member
 * docs under my properties go (owner-delete), mine under theirs go
 * (self-delete), and the mutual member_uids entries are removed. */
export async function removeAccountMember(uid, memberUid) {
  const acc = await getAccountShare(uid);
  if (!acc) return;
  const myTokens = await accountTokens(acc);
  await removeMemberDocs(myTokens, memberUid);
  let macc = null;
  try {
    macc = await getAccountShare(memberUid);
  } catch {
    macc = null;
  }
  if (macc) {
    const mt = await accountTokens(macc);
    await removeMemberDocs(mt, uid);
  }
  await updateDoc(doc(db, ACCOUNT_SHARES, uid), { member_uids: arrayRemove(memberUid) });
  if (macc) {
    try {
      await updateDoc(doc(db, ACCOUNT_SHARES, memberUid), { member_uids: arrayRemove(uid) });
    } catch (e) {
      console.warn('mutual member_uids cut skipped', e?.message);
    }
  }
}

/** Leave a partner's account share (self): my member docs under their
 * properties go (self-delete), theirs under mine go (owner-delete), and the
 * mutual member_uids entries are removed. */
export async function leaveAccountShare(uid, partnerUid) {
  const partnerAcc = await getAccountShare(partnerUid);
  if (!partnerAcc) return;
  const partnerTokens = await accountTokens(partnerAcc);
  await removeMemberDocs(partnerTokens, uid);
  const mine = await getAccountShare(uid);
  if (mine) {
    const myTokens = await accountTokens(mine);
    await removeMemberDocs(myTokens, partnerUid);
  }
  try {
    await updateDoc(doc(db, ACCOUNT_SHARES, partnerUid), { member_uids: arrayRemove(uid) });
  } catch (e) {
    console.warn('self-leave skipped', e?.message);
  }
  if (mine) {
    await updateDoc(doc(db, ACCOUNT_SHARES, uid), { member_uids: arrayRemove(partnerUid) });
  }
}

// ── user-defined attributes (docs/plans/user-attributes.md) ──────────────
// Definitions live at users/{uid}/attributes/{name} (advisory per-user lens:
// type + own-usage counts). Property values live in
// properties/{pid}.user_attributes — an open map; every write is one property
// update + the WRITER's definition upsert in the same batch (counts are
// per-writer, so define-on-use lands on the writer's own lens).
//
// account_shares/{uid}.attribute_defs (name → type) is the recs/filter union
// mirror for account-linked partners (user-attributes.md alignment #2) —
// maintained here, in the same flows as the definition helpers. Best-effort
// and profile-gated: no account share = nothing to mirror (one cheap read,
// skipped); the mirror is owner-written, so a partner can never fake another
// user's definitions.
const _mirrorInFlight = new Set();
async function syncAttributeDefsMirror(uid) {
  if (!uid || _mirrorInFlight.has(uid)) return;
  _mirrorInFlight.add(uid);
  try {
    if (!(await getDoc(doc(db, ACCOUNT_SHARES, uid))).exists()) return;
    const defs = await getDocs(collection(db, 'users', uid, 'attributes'));
    const attribute_defs = Object.fromEntries(defs.docs.map((d) => [d.id, d.data().type]));
    await updateDoc(doc(db, ACCOUNT_SHARES, uid), { attribute_defs });
  } catch (e) {
    console.warn('attribute_defs mirror sync skipped', e?.message);
  } finally {
    _mirrorInFlight.delete(uid);
  }
}

/** Live list of my attribute definitions. */
export function watchAttributes(uid, onData) {
  const q = query(collection(db, 'users', uid, 'attributes'));
  return onSnapshot(q, (snap) =>
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
}

/** Seed the starter definitions once per user (marker on users/{uid}).
 * Session-guarded; safe to call from every attributes surface on mount. */
const _seedInFlight = new Set();
export async function ensureDefaultAttributes(uid) {
  if (!uid || _seedInFlight.has(uid)) return;
  _seedInFlight.add(uid);
  try {
    const marker = (await getDoc(doc(db, 'users', uid))).data()?.attr_defaults_seeded;
    if (marker) return;
    const existing = (await getDocs(collection(db, 'users', uid, 'attributes'))).docs.map((d) => d.id);
    const missing = seedWrites(existing);
    const b = writeBatch(db);
    if (!missing.length) {
      // Seeded (or everything user-defined) — just record the marker.
      b.set(doc(db, 'users', uid), { attr_defaults_seeded: true }, { merge: true });
      await b.commit();
      return;
    }
    for (const d of missing) {
      b.set(doc(db, 'users', uid, 'attributes', d.name), {
        type: d.type,
        values: {},
        ...(d.knownKey ? { known_key: d.knownKey } : {}),
      });
    }
    b.set(doc(db, 'users', uid), { attr_defaults_seeded: true }, { merge: true });
    await b.commit();
  } finally {
    _seedInFlight.delete(uid);
  }
  syncAttributeDefsMirror(uid);
}

/** Set, overwrite, or delete (newValue null) an attribute value on a property.
 * One batch: single property write + the writer's definition upsert (count
 * delta). `type` is used only when the writer has no definition yet
 * (define-on-use); an existing definition keeps its type. Names and string
 * values are canonicalized (lowercase, letters/digits/spaces only). */
export async function saveAttributeValue(pid, { name, type, oldValue, newValue, attrs, currentValues }) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in required');
  const n = normalizeName(name);
  if (!n) throw new Error('Invalid attribute name');
  const oldV = normalizeAttrValue(oldValue);
  const newV = normalizeAttrValue(newValue);
  const next = { ...(attrs || {}) };
  if (newV === null || newV === undefined) delete next[n];
  else next[n] = newV;
  const values = applyValueDelta(currentValues, oldV, newV);
  const b = writeBatch(db);
  b.update(doc(db, PROPERTIES, pid), {
    user_attributes: next,
    updated_at: serverTimestamp(),
  });
  // Don't create an empty definition when deleting a value whose definition
  // doesn't exist (orphaned value from a deleted definition).
  const deleting = newV === null || newV === undefined;
  if (!(deleting && currentValues === undefined)) {
    b.set(doc(db, 'users', uid, 'attributes', n), { type, values }, { merge: true });
  }
  await b.commit();
  syncAttributeDefsMirror(uid);
}

/** One-time retrofit of old attribute names/values into the canonical style
 * (lowercase, letters/digits/spaces only). Property maps rekey to canonical
 * names/values — duplicate spellings merge (first value wins; an empty kept
 * value yields to a real one). Definitions with the same canonical name
 * merge: type degrades number→string on conflict, value usage counts sum.
 * Session-guarded; safe to call from every attributes surface on mount. */
const _styleMigrateInFlight = new Set();
export async function migrateAttributeStyle(uid) {
  if (!uid || _styleMigrateInFlight.has(uid)) return;
  _styleMigrateInFlight.add(uid);
  try {
    const marker = (await getDoc(doc(db, 'users', uid))).data()?.attr_style_migrated;
    if (marker) return;
    const propSnap = await getDocs(query(collection(db, PROPERTIES), where('owner_uid', '==', uid)));
    const defSnap = await getDocs(collection(db, 'users', uid, 'attributes'));
    const b = writeBatch(db);
    let touched = false;

    for (const p of propSnap.docs) {
      const attrs = p.data().user_attributes;
      if (!attrs || !Object.keys(attrs).length) continue;
      const next = {};
      let dirty = false;
      for (const [rawName, rawVal] of Object.entries(attrs)) {
        const name = normalizeName(rawName);
        if (!name) continue;
        const val = normalizeAttrValue(rawVal);
        if (name !== rawName || val !== rawVal) dirty = true;
        if (next[name] === undefined) next[name] = val;
        else if (next[name] === '' && val !== '') next[name] = val;
      }
      if (dirty) {
        b.update(p.ref, { user_attributes: next, updated_at: serverTimestamp() });
        touched = true;
      }
    }

    const merged = new Map(); // canonical name → {type, values, ids}
    for (const d of defSnap.docs) {
      const name = normalizeName(d.id);
      if (!name) continue;
      const data = d.data();
      const e = merged.get(name) || { type: NUMBER, values: new Map(), ids: [] };
      if (data.type !== NUMBER) e.type = STRING;
      for (const [v, c] of Object.entries(data.values || {})) {
        const key = String(normalizeAttrValue(v));
        e.values.set(key, (e.values.get(key) || 0) + c);
      }
      e.ids.push(d.id);
      merged.set(name, e);
    }
    for (const [name, e] of merged) {
      const values = Object.fromEntries(e.values);
      const oneCanonical = e.ids.length === 1 && e.ids[0] === name;
      const valuesClean = Object.keys(values).every((v) => normalizeAttrValue(v) === v);
      if (oneCanonical && valuesClean) continue;
      touched = true;
      b.set(doc(db, 'users', uid, 'attributes', name), { type: e.type, values }, { merge: true });
      for (const id of e.ids) if (id !== name) b.delete(doc(db, 'users', uid, 'attributes', id));
    }

    b.set(doc(db, 'users', uid), { attr_style_migrated: true }, { merge: true });
    await b.commit();
  } catch (e) {
    console.warn('attribute style migration failed', e);
  } finally {
    _styleMigrateInFlight.delete(uid);
  }
  syncAttributeDefsMirror(uid);
}

/** Define an attribute without setting a value anywhere (account manager). */
export async function defineAttribute(name, type) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in required');
  const n = normalizeName(name);
  if (!n) throw new Error('Invalid attribute name');
  await setDoc(doc(db, 'users', uid, 'attributes', n), { type, values: {} }, { merge: true });
  syncAttributeDefsMirror(uid);
}

/** Rename a definition + rekey user_attributes across my properties that
 * have the old key (collisions skipped). One batch. */
export async function renameAttribute(oldName, newName) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in required');
  const n = normalizeName(newName);
  if (!n || n === oldName) return;
  const oldRef = doc(db, 'users', uid, 'attributes', oldName);
  const oldSnap = await getDoc(oldRef);
  if (!oldSnap.exists()) return;
  const props = await getDocs(query(collection(db, PROPERTIES), where('owner_uid', '==', uid)));
  const b = writeBatch(db);
  let rekeyed = 0;
  for (const p of props.docs) {
    const attrs = p.data().user_attributes;
    if (!attrs || !(oldName in attrs)) continue;
    if (n in attrs) continue; // collision — keep the old key on this property
    const next = { ...attrs };
    next[n] = next[oldName];
    delete next[oldName];
    b.update(p.ref, { user_attributes: next, updated_at: serverTimestamp() });
    rekeyed++;
  }
  b.set(doc(db, 'users', uid, 'attributes', n), oldSnap.data());
  b.delete(oldRef);
  await b.commit();
  syncAttributeDefsMirror(uid);
  return rekeyed;
}

/** Change a definition's type (filter ops follow it; stored values keep their
 * own type — comparisons are typeof-safe). */
export async function retypeAttribute(name, type) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in required');
  await updateDoc(doc(db, 'users', uid, 'attributes', name), { type });
  syncAttributeDefsMirror(uid);
}

/** Delete a definition only — property values remain (orphaned). */
export async function deleteAttribute(name) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in required');
  await deleteDoc(doc(db, 'users', uid, 'attributes', name));
  syncAttributeDefsMirror(uid);
}
